import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import type { ReactNode } from "react";
import type { HelpArticleSummary } from "@/interfaces/help";
import { splitHelpSearchHighlight } from "@/utils/help-search-helper";
import { formatVideoDuration } from "@/utils/help-video-helper";

export interface HelpArticleCardProps {
  article: HelpArticleSummary;
  onSelect: (slug: string) => void;
  /** Search query whose matches are marked in the title and summary. */
  highlight?: string;
}

/**
 * "Pro" / "Enterprise", or null for an ungated article. GFG-687 (Judd owns the visual design of
 * this badge) — this only indicates the gate exists. The entitlement check and upgrade prompt
 * that would act on it are GFG-688 and are explicitly out of scope here.
 */
function planGateLabel(planGate: HelpArticleSummary["planGate"]): string | null {
  if (planGate === "pro") return "Pro";
  if (planGate === "ent") return "Enterprise";
  return null;
}

/** Pill classes per the badge's plan gate — `.hc-badge.pro`/`.hc-badge.ent`. */
function planGateBadgeClass(planGate: HelpArticleSummary["planGate"]): string {
  return planGate === "pro" ? "bg-good-night-4 text-good-night" : "bg-good-ochre-4 text-good-ochre";
}

/** `text` with each run matching `query` wrapped in a `.hc-mark` highlight. */
function renderHighlighted(text: string, query: string): ReactNode[] {
  let offset = 0;
  return splitHelpSearchHighlight(text, query).map((part) => {
    const start = offset;
    offset += part.text.length;
    return part.match ? (
      <mark key={start} className="rounded bg-light-dust px-0.5 text-inherit">
        {part.text}
      </mark>
    ) : (
      part.text
    );
  });
}

/**
 * One article in the Support panel's three-article cut, rendered as a prototype row ("rules, not
 * cards"): a smoke top rule, a bottom rule on the last row, no card border. Video articles show
 * their duration here, before the click — a reader must never be surprised into starting a video
 * (W2 spec). The badge appears only for articles with a video link; the article view plays it.
 */
export function HelpArticleCard({ article, onSelect, highlight }: HelpArticleCardProps) {
  const gateLabel = planGateLabel(article.planGate);

  return (
    <button
      type="button"
      data-testid="help-article-card"
      onClick={() => onSelect(article.slug)}
      className="group block w-full border-smoke border-t py-[18px] text-left font-lato last:border-b"
    >
      <span
        data-testid="help-article-card-title"
        className="block font-bold text-[14px] text-rodeo-black leading-snug transition-colors group-hover:text-dust-ink"
      >
        {highlight === undefined ? article.title : renderHighlighted(article.title, highlight)}
        {article.videoSeconds !== null && (
          <span className="ml-[7px] inline-flex items-center gap-[3px] align-[1px] font-bold text-[11px] text-dust-ink tracking-[.04em]">
            <PlayCircleOutlineIcon className="text-[14px]" aria-hidden="true" />
            {formatVideoDuration(article.videoSeconds)}
          </span>
        )}
        {gateLabel && (
          <span
            className={`ml-[7px] inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 align-[1px] font-bold text-[11px] uppercase tracking-[.05em] ${planGateBadgeClass(article.planGate)}`}
          >
            {gateLabel}
          </span>
        )}
      </span>
      <span className="mt-[3px] block text-[13px] text-graphite leading-normal">
        {highlight === undefined ? article.summary : renderHighlighted(article.summary, highlight)}
      </span>
    </button>
  );
}

export default HelpArticleCard;
